// Step 4, "Export & properties": the Wannier90 / WannierBerri export of the fit and its band check (DFT vs TB vs Wannier,
// the desktop's ResultsPage "bands"), and the way to Properties, where the fit's properties are computed (Berry
// curvature, spin Berry curvature, AHC, SHC, Wilson loops, Chern numbers moved there in M3f; an illustrative project
// continues there with its own calculations). Property runs made here before M3f stay viewable below.
import { Alert, Anchor, Button, Card, Code, Group, Image, Loader, NumberInput, SimpleGrid, Stack, Table, Tabs, Text } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ErrorBox, notifyError } from "../../components/Errors";
import { CancelButton, JobProgress } from "../../components/Jobs";
import { RefLinks } from "../../components/RefLinks";
import { limitError, PreviousRuns } from "../../components/RunCard";
import { download, fileBlob, post, type Job, type ProjectDetail } from "../../lib/api";
import { useLiveJob, useMe } from "../../lib/hooks";
import { continueInProperties } from "../../lib/properties";
import { tagged, useReferences } from "../../lib/references";
import { exportKmesh, type DftResult, type ExportResult, type FitResult, type PropItem, type PropResult, type SkForms,
  type SkMemory } from "../../lib/skfitter";

export const BAND_CHECK = { kind: "bands", source: "wb" };

export function PropertiesStep({ project, forms, dft, fitJob, mem, remember, onBack }: {
  project: ProjectDetail; forms: SkForms; dft: DftResult; fitJob: Job; mem: SkMemory; update?: (m: Partial<SkMemory>) => void;
  remember: (m: Partial<SkMemory>) => void; onBack: () => void;
}) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { data: refSections } = useReferences();            // "[n]" beside each method (skfitter/skfitter_references)
  const refs = (tag: string) => tagged(refSections, "skfitter", `skfitter-${tag}`);
  const fit = fitJob.result as FitResult;
  const lattice = dft.meta.lattice;
  const limits = project.limits ?? me?.limits;
  const exportAllowed = !!limits?.["properties.geometric.enabled"];    // an example may run with Advanced
  const propsAllowed = !!limits?.["properties.enabled"];
  const nav = useNavigate();
  const toProps = useMutation({
    mutationFn: () => continueInProperties(fitJob.id),
    onSuccess: (p) => { qc.invalidateQueries({ queryKey: ["projects"] }); qc.invalidateQueries({ queryKey: ["me"] }); nav(`/projects/${p.id}`); },
    onError: (e) => notifyError(e),
  });

  // -- Wannier export (belongs to the fit it was made from) ------------------------------------------------------
  const [kmesh, setKmesh] = useState<number[]>(() => mem.export_kmesh ?? exportKmesh(lattice));
  const { job: exportJob } = useLiveJob(mem.export?.fit_job === fitJob.id ? mem.export.job : undefined);
  const exported = exportJob?.status === "succeeded" ? (exportJob.result as ExportResult) : undefined;
  const ref = { dft: dft.dft, elements: dft.meta.elements, cfg: fit.cfg, seed: fit.seed,
    param_file: fit.param_file ?? fit.files?.[`${fit.seed}_fit.json`] };
  const doExport = useMutation({
    mutationFn: () => post<Job>("/jobs", { kind: "skfitter.export", project_id: project.id, params: { ...ref, kmesh } }),
    onSuccess: (j) => { remember({ export: { job: j.id, fit_job: fitJob.id } }); qc.invalidateQueries({ queryKey: ["project", project.id] }); },
  });
  const exportBusy = exportJob && (exportJob.status === "queued" || exportJob.status === "running");

  // -- the band check of the export (and the property runs made here before M3f) --------------------------------
  const { job: propsJob } = useLiveJob(mem.props_job);
  const check = useMutation({
    mutationFn: () => post<Job>("/jobs", { kind: "skfitter.properties", project_id: project.id,
      params: { ...ref, specs: [BAND_CHECK], wannier: exported!.files } }),
    onSuccess: (j) => { remember({ props_job: j.id }); qc.invalidateQueries({ queryKey: ["me"] }); },
  });
  const propsBusy = propsJob && (propsJob.status === "queued" || propsJob.status === "running");
  const results = propsJob?.result as PropResult | undefined;
  const checkWhy = !exported ? "Export the Wannier files first." : !forms.wannierberri.ok ? forms.wannierberri.message : "";
  const proceed = mem.props_continue;                    // an illustrative project continues in Properties

  return (
    <Stack mt="md" gap="md">
      <Card withBorder data-testid="properties-link">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <div>
            <Text fw={600}>Properties of this fit</Text>
            {proceed ? (
              <Text size="sm">This illustrative project continues in Properties: <b>{proceed}</b>. The calculations are set up there
                for your fit.</Text>
            ) : (
              <Text size="sm">Bands with weights, DOS, effective masses, Berry curvature, Wilson loops, Chern numbers and the anomalous
                and spin Hall conductivities of this fit are computed in Properties - for any model (fits, Wannier90, uploads), with
                fields and gap changes.</Text>
            )}
            {!propsAllowed && <Text size="xs" c="orange" mt={4}>Properties is not part of your plan.</Text>}
          </div>
          <Button variant="filled" onClick={() => toProps.mutate()} loading={toProps.isPending} disabled={!propsAllowed}
            data-testid="continue-in-properties" style={{ flexShrink: 0 }}>
            {proceed ? "Continue in Properties →" : "Properties of this model →"}
          </Button>
        </Group>
      </Card>

      <Card withBorder>
        <Text fw={600}>Wannier90 / WannierBerri export (optional)<RefLinks refs={refs("wannier")} /></Text>
        {!exportAllowed ? (
          <ErrorBox error={limitError({ detail: "The Wannier export is not included in your plan.",
            limit: "properties.geometric.enabled", allowed: 0, requested: 1 })} />
        ) : (
          <>
            <Text size="sm" c="dimmed">
              Writes {fit.seed}_hr.dat, {fit.seed}_tb.dat and {fit.seed}_centres.xyz from the fitted Hamiltonian, ready for WannierBerri
              or Wannier90-based codes. H(R) is obtained on a k-mesh: use at least twice the longest hopping range (in cells) along
              each axis, and 1 along a vacuum direction.
            </Text>
            <Group mt="xs" align="flex-end" gap="xs">
              <Kmesh label="Export k-mesh" value={kmesh} onChange={setKmesh} />
              <Button size="xs" onClick={() => doExport.mutate()} loading={doExport.isPending} disabled={!!exportBusy}>
                {exported ? "Export again" : "Export Wannier files"}
              </Button>
            </Group>
            <ErrorBox error={doExport.error} />
            {exportBusy && <JobProgress job={exportJob!} />}
            {exportJob?.error && <ErrorBox error={new Error(exportJob.error)} />}
            <Text size="sm" mt={4} c={exported ? "teal" : "dimmed"}>
              {exported
                ? <>Exported (k-mesh {exported.kmesh.join("×")}):{" "}
                  {(["tb", "hr", "centres"] as const).map((r) => (
                    <Anchor key={r} component="button" size="sm" mr="xs"
                      onClick={() => download(`/files/${exported.files[r]}/download`, exported.names[r]).catch(notifyError)}>{exported.names[r]}</Anchor>
                  ))}</>
                : "Not exported yet."}
            </Text>
            <Group mt="sm" justify="space-between" wrap="nowrap" align="flex-end">
              <div>
                <Text fw={600} size="sm">Band check: DFT vs tight-binding vs Wannier</Text>
                <Text size="xs" c="dimmed">The whole chain fit → export → WannierBerri: the Wannier bands must lie on top of the
                  tight-binding bands.{checkWhy ? ` ${checkWhy}` : ""}</Text>
              </div>
              <Button size="xs" variant="light" onClick={() => check.mutate()} loading={check.isPending}
                disabled={!!checkWhy || !!propsBusy} data-testid="band-check">Run the band check</Button>
            </Group>
            <ErrorBox error={check.error} />
          </>
        )}
      </Card>

      <Group justify="space-between">
        <Button variant="default" onClick={onBack}>← Back to the fit</Button>
      </Group>

      {propsJob && (
        <Card withBorder>
          <Group justify="space-between" mb="xs">
            <Text fw={600}>Results</Text>
            <CancelButton job={propsJob} label="Stop after the current step" />
          </Group>
          <JobProgress job={propsJob} />
          {propsJob.error && <ErrorBox error={new Error(propsJob.error)} />}
          {results?.stopped && <Alert color="yellow" p="xs" mb="xs"><Text size="sm">{results.stopped} The finished results are kept.</Text></Alert>}
          {results?.unit_hint && <Text size="sm" c="dimmed">{results.unit_hint}</Text>}
          {results?.items?.length ? <ResultTabs items={results.items} /> : propsBusy ? <Text size="sm" c="dimmed">The results appear here as each one finishes.</Text> : null}
        </Card>
      )}
      <PreviousRuns project={project} kind="skfitter.properties" current={mem.props_job} onOpen={(id) => remember({ props_job: id })}
        describe={describeStep4} />
    </Stack>
  );
}

/** A run of step 4 in the list: the band check, or (made before M3f) a set of properties. */
export function describeStep4(p: Record<string, any>): string {
  const specs = (p.specs ?? []) as { kind: string }[];
  if (specs.length && specs.every((s) => s.kind === "bands")) return `${p.seed ?? ""} · band check`;
  return `${p.seed ?? ""} · ${specs.length} propert${specs.length === 1 ? "y" : "ies"} (before Properties)`;
}

function Kmesh({ label, value, onChange }: { label: string; value: number[]; onChange: (v: number[]) => void }) {
  return (
    <div>
      <Text size="xs" fw={500} mb={2}>{label}</Text>
      <Group gap={4} wrap="nowrap">
        {[0, 1, 2].map((i) => (
          <NumberInput key={i} size="xs" w={70} min={1} max={400} value={value[i]} aria-label={`${label} ${"abc"[i]}`} allowDecimal={false}
            onChange={(v) => typeof v === "number" && onChange(value.map((x, j) => (j === i ? v : x)))} />
        ))}
      </Group>
    </div>
  );
}

export function ResultTabs({ items }: { items: PropItem[] }) {
  const [tab, setTab] = useState<string | null>(items[items.length - 1]?.tag ?? null);
  useEffect(() => { setTab(items[items.length - 1]?.tag ?? null); }, [items.length]);   // show the newest result
  return (
    <Tabs value={tab} onChange={setTab} mt="xs">
      <Tabs.List>
        {items.map((it) => (
          <Tabs.Tab key={it.tag} value={it.tag} color={it.error ? "red" : undefined} title={it.label}>
            {it.label.length > 48 ? it.label.slice(0, 48) + "…" : it.label}
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {items.map((it) => <Tabs.Panel key={it.tag} value={it.tag} pt="sm"><ResultTab item={it} /></Tabs.Panel>)}
    </Tabs>
  );
}

/** 2 -> "2nd", 3 -> "3rd", 11 -> "11th", ... */
export function ordinal(n: number): string {
  const t = n % 100, u = n % 10;
  return `${n}${t >= 11 && t <= 13 ? "th" : u === 1 ? "st" : u === 2 ? "nd" : u === 3 ? "rd" : "th"}`;
}

function StoredImage({ fileId, alt }: { fileId: string; alt: string }) {
  const q = useQuery({ queryKey: ["file-blob", fileId], queryFn: () => fileBlob(`/files/${fileId}/download`), staleTime: Infinity });
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!q.data) return;
    const u = URL.createObjectURL(q.data);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [q.data]);
  if (q.error) return <ErrorBox error={q.error} />;
  return url ? <Image src={url} alt={alt} fit="contain" /> : <Loader size="sm" />;
}

/** A failed property: the error, and its Python traceback behind a toggle (closed at first). */
export function FailedResult({ item }: { item: PropItem }) {
  const [open, setOpen] = useState(false);
  return (
    <Stack gap={4}>
      <Text fw={600}>{item.label}</Text>
      <Text size="sm" c="red">This calculation failed: {item.error}</Text>
      {item.traceback ? (
        <>
          <Anchor component="button" type="button" size="xs" ta="left" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            {open ? "▾ Hide the full traceback" : "▸ Show the full traceback"}
          </Anchor>
          {open && <Code block style={{ maxHeight: 360, overflow: "auto", fontSize: 11 }}>{item.traceback}</Code>}
        </>
      ) : (
        <Text size="xs" c="dimmed">The full traceback is in the run's log (Jobs page, once the run has finished).</Text>
      )}
    </Stack>
  );
}

export function ResultTab({ item }: { item: PropItem }) {
  if (item.error) return <FailedResult item={item} />;
  const tb = item.table;
  const name = (ext: string) => `${item.tag}.${ext}`;
  return (
    <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
      <div>{item.png ? <StoredImage fileId={item.png} alt={item.label} /> : <Text c="dimmed">No plot.</Text>}</div>
      <Stack gap="xs">
        <Text fw={600}>{item.label}</Text>
        {!!item.summary?.length && (
          <Table withRowBorders={false} verticalSpacing={2}>
            <Table.Tbody>
              {item.summary.map(([k, v]) => (
                <Table.Tr key={k}><Table.Td><Text size="xs" c="dimmed">{k}</Text></Table.Td><Table.Td><Text size="sm">{v}</Text></Table.Td></Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
        {item.notes?.map((n) => <Text key={n} size="xs" c="dimmed">• {n}</Text>)}
        {tb && tb.rows.length > 0 && (
          <>
            <Text size="sm" fw={600}>Data ({tb.n_total} rows{tb.step && tb.step > 1 ? `, every ${ordinal(tb.step)} shown` : ""})</Text>
            <Table.ScrollContainer minWidth={300} mah={300}>
              <Table stickyHeader striped verticalSpacing={1} fz="xs">
                <Table.Thead><Table.Tr>{tb.headers.map((h) => <Table.Th key={h}>{h}</Table.Th>)}</Table.Tr></Table.Thead>
                <Table.Tbody>{tb.rows.map((r, i) => <Table.Tr key={i}>{r.map((c, j) => <Table.Td key={j}>{c}</Table.Td>)}</Table.Tr>)}</Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </>
        )}
        <Group gap="xs">
          {item.png && <Button size="xs" variant="light" onClick={() => download(`/files/${item.png}/download`, name("png")).catch(notifyError)}>Plot (.png)</Button>}
          {item.dat && <Button size="xs" variant="light" onClick={() => download(`/files/${item.dat}/download`, name("dat")).catch(notifyError)}>Data (.dat)</Button>}
          {item.elapsed != null && <Text size="xs" c="dimmed">{item.elapsed.toFixed(1)} s</Text>}
        </Group>
      </Stack>
    </SimpleGrid>
  );
}
